import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MoreVertical, Edit, Trash2, Plus, Upload, X } from 'lucide-react';
import SearchableSelect from './SearchableSelect';

const VendorManagement = ({ isViewer }) => {
  const [vendors, setVendors] = useState([]);
  const [glAccounts, setGLAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  
  const [formData, setFormData] = useState({
    name: '',
    glAccountId: '',
    isActive: true,
    noReceiptRequired: false,
    lookups: []
  });

  const [newLookup, setNewLookup] = useState('');

  // Dropdown states
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openDropdownVendor, setOpenDropdownVendor] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (includeInactive) params.append('includeInactive', 'true');
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);

      const response = await axios.get(`/api/vendors?${params}`);
      setVendors(response.data.vendors);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
      setError('');
    } catch (error) {
      console.error('Error fetching vendors:', error);
      setError('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, includeInactive, pagination.page, pagination.limit]);

  const fetchGLAccounts = async () => {
    try {
      const response = await axios.get('/api/gl-accounts?limit=1000');
      setGLAccounts(response.data.glAccounts || []);
    } catch (error) {
      console.error('Error fetching GL accounts:', error);
    }
  };

  useEffect(() => {
    fetchGLAccounts();
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [searchTerm, includeInactive]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId &&
          !event.target.closest('.relative') &&
          !event.target.closest('.dropdown-menu')) {
        setOpenDropdownId(null);
        setOpenDropdownVendor(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Vendor name is required');
      return;
    }

    try {
      if (editingVendor) {
        await axios.put(`/api/vendors/${editingVendor.id}`, formData);
        setSuccess('Vendor updated successfully');
      } else {
        await axios.post('/api/vendors', formData);
        setSuccess('Vendor created successfully');
      }
      
      setShowModal(false);
      setEditingVendor(null);
      setFormData({
        name: '',
        glAccountId: '',
        isActive: true,
        noReceiptRequired: false,
        lookups: []
      });
      fetchVendors();
      setError('');
    } catch (error) {
      console.error('Error saving vendor:', error);
      setError(error.response?.data?.error || 'Failed to save vendor');
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name,
      glAccountId: vendor.glAccountId || '',
      isActive: vendor.isActive,
      noReceiptRequired: vendor.noReceiptRequired || false,
      lookups: vendor.lookups || []
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Are you sure you want to delete "${vendor.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`/api/vendors/${vendor.id}`);
      setSuccess('Vendor deleted successfully');
      fetchVendors();
      setError('');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setError(error.response?.data?.error || 'Failed to delete vendor');
    }
  };

  const handleNewVendor = () => {
    setEditingVendor(null);
    setFormData({
      name: '',
      glAccountId: '',
      isActive: true,
      lookups: []
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setFormData({
      name: '',
      glAccountId: '',
      isActive: true,
      lookups: []
    });
    setError('');
  };

  const addLookup = () => {
    if (newLookup.trim() && !formData.lookups.includes(newLookup.trim())) {
      setFormData({
        ...formData,
        lookups: [...formData.lookups, newLookup.trim()]
      });
      setNewLookup('');
    }
  };

  const removeLookup = (lookupToRemove) => {
    setFormData({
      ...formData,
      lookups: formData.lookups.filter(lookup => lookup !== lookupToRemove)
    });
  };

  const handleLookupKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addLookup();
    }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
    setUploadResults(null);
  };

  const handleUploadCSV = async () => {
    if (!uploadFile) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('csvFile', uploadFile);

    try {
      const response = await axios.post('/api/vendors/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadResults(response.data);
      setSuccess(response.data.message);
      fetchVendors();

      // Clear file input
      setUploadFile(null);
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setError(error.response?.data?.error || 'Failed to upload CSV');
    } finally {
      setUploading(false);
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadResults(null);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Vendors</h3>
        {!isViewer && (
          <div className="flex gap-3">
            <button
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
              onClick={() => setShowUploadModal(true)}
            >
              <Upload className="h-4 w-4" />
              Upload CSV
            </button>
            <button
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center gap-2"
              onClick={handleNewVendor}
            >
              <Plus className="h-4 w-4" />
              Add Vendor
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder="Search vendors..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="includeInactive"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="includeInactive" className="text-sm font-medium text-gray-700">Include inactive</label>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">Loading vendors...</div>
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No vendors found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GL Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lookups</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {vendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">{vendor.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      {vendor.glAccount ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">{vendor.glAccount.accountNumber}</div>
                          <div className="text-xs text-gray-500">
                            {vendor.glAccount.accountName}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No GL Account</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {vendor.lookups && vendor.lookups.length > 0 ? (
                          vendor.lookups.map((lookup, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {lookup}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">No lookups</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        vendor.noReceiptRequired
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {vendor.noReceiptRequired ? 'Not Required' : 'Required'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        vendor.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {vendor.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(vendor.createdAt).toLocaleDateString('en-US')}
                    </td>
                    <td className="px-6 py-4">
                      {!isViewer && (
                        <div className="relative">
                          <button
                            className="p-1 rounded-md hover:bg-gray-100 transition-colors duration-200"
                            onClick={(e) => {
                              if (openDropdownId === vendor.id) {
                                setOpenDropdownId(null);
                                setOpenDropdownVendor(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setDropdownPosition({
                                  top: rect.bottom,
                                  left: rect.left - 180
                                });
                                setOpenDropdownId(vendor.id);
                                setOpenDropdownVendor(vendor);
                              }
                            }}
                          >
                            <MoreVertical className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && vendors.length > 0 && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total vendors)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
              </h3>
              <button 
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold" 
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter vendor name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">GL Account</label>
                <SearchableSelect
                  value={formData.glAccountId}
                  onChange={(value) => setFormData({ ...formData, glAccountId: value })}
                  options={glAccounts}
                  getOptionLabel={(account) => `${account.accountNumber} - ${account.accountName}`}
                  getOptionValue={(account) => account.id}
                  filterOption={(account, search) => {
                    const searchLower = search.toLowerCase();
                    return account.accountNumber.toLowerCase().includes(searchLower) ||
                           account.accountName.toLowerCase().includes(searchLower);
                  }}
                  placeholder="Select GL Account (optional)"
                  searchPlaceholder="Search by code or name..."
                  emptyLabel="No GL Account"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Lookups</label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                      value={newLookup}
                      onChange={(e) => setNewLookup(e.target.value)}
                      onKeyPress={handleLookupKeyPress}
                      placeholder="Add lookup string (e.g. 'amazon mktpl')"
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={addLookup}
                      disabled={!newLookup.trim() || formData.lookups.includes(newLookup.trim())}
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.lookups.map((lookup, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                      >
                        {lookup}
                        <button
                          type="button"
                          onClick={() => removeLookup(lookup)}
                          className="text-blue-600 hover:text-blue-800 font-bold text-lg leading-none"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    Add strings that appear in transaction descriptions to automatically assign this vendor
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Active</label>
                </div>
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="noReceiptRequired"
                    checked={formData.noReceiptRequired}
                    onChange={(e) => setFormData({ ...formData, noReceiptRequired: e.target.checked })}
                    className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="noReceiptRequired" className="text-sm font-medium text-gray-700">
                    No Receipt Required
                    <span className="block text-xs text-gray-500 font-normal">
                      Transactions for this vendor will be auto-matched without requiring receipt submission
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button 
                  type="button" 
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200" 
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  {editingVendor ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Upload Vendors CSV</h3>
              <button
                onClick={closeUploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                CSV should have columns: <strong>name</strong>, <strong>glAccount</strong> (optional), <strong>lookups</strong> (optional, comma-separated)
              </p>
              <p className="text-xs text-gray-500 mb-4">
                Example: name,glAccount,lookups<br />
                "ABC Company","6000 - Cost of Goods Sold","ABC,ABC Inc,ABC Corp"
              </p>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>

            {uploadResults && (
              <div className="mb-4 p-4 bg-gray-50 rounded-md">
                <h4 className="font-medium mb-2">Upload Results:</h4>
                <div className="text-sm space-y-1">
                  <p>Total processed: {uploadResults.summary.total}</p>
                  <p className="text-green-600">Created: {uploadResults.summary.created}</p>
                  <p className="text-blue-600">Updated: {uploadResults.summary.updated}</p>
                  <p className="text-red-600">Errors: {uploadResults.summary.errors}</p>
                </div>
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="font-medium text-sm text-red-600 mb-1">Errors:</p>
                    <div className="max-h-40 overflow-y-auto text-xs text-red-600">
                      {uploadResults.errors.map((err, idx) => (
                        <div key={idx}>{err}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                onClick={closeUploadModal}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200"
              >
                Close
              </button>
              <button
                onClick={handleUploadCSV}
                disabled={!uploadFile || uploading}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors duration-200 disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed positioned dropdown menu */}
      {openDropdownId && openDropdownVendor && (
        <div
          className="fixed w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 dropdown-menu"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <div className="py-1">
            <button
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
              onClick={() => {
                handleEdit(openDropdownVendor);
                setOpenDropdownId(null);
                setOpenDropdownVendor(null);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Vendor
            </button>
            <button
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
              onClick={() => {
                setOpenDropdownId(null);
                setOpenDropdownVendor(null);
                handleDelete(openDropdownVendor);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Vendor
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;